import { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import { URL_SERVER } from '../utils/Utils';
import { ModalType, OrganizationRoleType, Usuario, UsuarioShow } from '../utils/Types';
import { useNightMode } from "../hooks/useNightMode";
import { useTheme } from "../context/AppThemeContext";

const useAdminPanel = () => {
  const { isNight, toggleNightMode } = useTheme();
  const [users, setUsers] = useState<UsuarioShow[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationRoleType[]>([]);
  const [roles, setRoles] = useState<OrganizationRoleType[]>([]);
  const [usersFiltered, setUsersFiltered] = useState<UsuarioShow[]>([]);
  const [organizationsFiltered, setOrganizationsFiltered] = useState<OrganizationRoleType[]>([]);
  const [rolesFiltered, setRolesFiltered] = useState<OrganizationRoleType[]>([]);

  const [modalOpen, setModalOpen] = useState<ModalType | null>(null);

  const [currentUser, setCurrentUser] = useState<Partial<Usuario>>({});
  const [currentOrganization, setCurrentOrganization] = useState<Partial<OrganizationRoleType>>({});
  const [currentRole, setCurrentRole] = useState<Partial<OrganizationRoleType>>({});

  const [searchUserTerm, setSearchUserTerm] = useState('');
  const [searchOrganizationTerm, setSearchOrganizationsTerm] = useState('');
  const [searchRolesTerm, setSearchRolesTerm] = useState('');

  const storageToken = secureLocalStorage.getItem("token");
  const token = typeof storageToken === "string" ? storageToken : null;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
  const storageUsers = secureLocalStorage.getItem("users");
  const storageRoles = secureLocalStorage.getItem("roles");
  const storageOrganizations = secureLocalStorage.getItem("organizations");

  if (storageUsers) {
    try {
      const parsedUsers: UsuarioShow[] = JSON.parse(storageUsers as string);
      setUsers(parsedUsers);
    } catch (err) {
      console.error("Error parsing users from local storage", err);
      fetchUsers();
    }
  } else {
    fetchUsers();
  }

  if (storageRoles) {
    try {
      const parsedRoles: OrganizationRoleType[] = JSON.parse(storageRoles as string);
      setRoles(parsedRoles);
    } catch (err) {
      console.error("Error parsing roles from local storage", err);
      fetchRoles();
    }
  } else {
    fetchRoles();
  }

  if (storageOrganizations) {
    try {
      const parsedOrgs: OrganizationRoleType[] = JSON.parse(storageOrganizations as string);
      setOrganizations(parsedOrgs);
    } catch (err) {
      console.error("Error parsing organizations from local storage", err);
      fetchOrganizations();
    }
  } else {
    fetchOrganizations();
  }
};

  const fetchUsers = async () => {
    const usersResponse = await axios.get(`${URL_SERVER}/v3/users`,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (usersResponse?.data) {
      setUsers(usersResponse.data.data.map((f: any) => ({
        id: f.id,
        name: String(f.name),
        surename: String(f.surename),
        username: String(f.username),
        organization: String(f.organization.name),
      })));
    }

    secureLocalStorage.setItem("users", users);
  };

  const fetchOrganizations = async () => {
    const orgsResponse = await axios.get(`${URL_SERVER}/v3/organizations`,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (orgsResponse?.data) {
      setOrganizations(
        orgsResponse.data.data.map((f: any) => (
          {
            id: String(f.id),
            name: String(f.name)
          }))
      );
    }
    secureLocalStorage.setItem("organizations", organizations);
  };


  const fetchRoles = async () => {
    const rolesResponse = await axios.get(`${URL_SERVER}/v3/roles`,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (rolesResponse?.data) {
      setRoles(
        rolesResponse.data.data.map((f: any) => (
          {
            id: String(f.id),
            name: String(f.name)
          }))
      );
    }
    secureLocalStorage.setItem("roles", roles);
  };

  const openModal = (type: ModalType) => {
    setModalOpen(type);
    setCurrentUser({});
    setCurrentRole({});
    setCurrentOrganization({});
    setSearchRolesTerm("");
    setSearchOrganizationsTerm("");
    setSearchUserTerm("");
  };

  const closeModal = () => setModalOpen(null);

  const handleUserChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCurrentUser(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchUser = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchUserTerm(e.target.value);
  };

  const submitUser = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentUser.role_id || !currentUser.organization_id) {
      alert("Faltan datos obligatorios.");
      return;
    }

    try {
      const userPayload = {
        ...currentUser,
        role_id: parseInt(currentUser.role_id as any),
        organization_id: parseInt(currentUser.organization_id as any),
        verified: false,
      };

      const response = await axios.post(`${URL_SERVER}/v3/users`, userPayload, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });

      if (response.data) {
        secureLocalStorage.removeItem("users");
      }

    } catch (error: any) {
      console.error("Error al crear el usuario:", error.response?.data || error.message);
    }

    await fetchData();
    closeModal();
  };

  const submitUserUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentUser.role_id || !currentUser.organization_id) {
      alert("Faltan datos obligatorios.");
      return;
    }

    try {
      const userPayload = {
        ...currentUser,
        role_id: parseInt(currentUser.role_id as any),
        organization_id: parseInt(currentUser.organization_id as any),
        verified: false,
      };

      const response = await axios.put(`${URL_SERVER}/v3/users/${currentUser.id}`, userPayload, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });

      if (response.data) {
        secureLocalStorage.removeItem("users");
      }
    } catch (error: any) {
      console.error("Error al actualizar el usuario:", error.response?.data || error.message);
    }

    await fetchData();
    closeModal();
  };

  const deleteUser = async (id: any) => {
    try {
      const response = await axios.delete(`${URL_SERVER}/v3/users/${id}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });

    } catch (err) {
      console.error("Error al eliminar usuario:", err);
    }

    await fetchData();
    closeModal();
  };

  const restoreUser = async (id: any) => {
    try {
      await axios.post(`${URL_SERVER}/v3/users/${id}/restore`, null, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
    } catch (err) {
      console.error("Error al restaurar usuario:", err);
    }

    await fetchData();
    closeModal();
  };

  const getFilteredUsers = (e: FormEvent) => {
    e.preventDefault();
    const aux = users.filter(u =>
      u.name.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
      u.username.toLowerCase().includes(searchUserTerm.toLowerCase())
    );
    setUsersFiltered(aux);
  };

  const handleRoleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCurrentRole(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchRoles = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchRolesTerm(e.target.value);
  };

  const submitRole = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentRole.name) {
      alert("Faltan datos obligatorios.");
      return;
    }

    try {
      const payload = {
        name: currentRole.name
      };

      const response = await axios.post(`${URL_SERVER}/v3/roles`, payload, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });

      if (response.data) {
        secureLocalStorage.removeItem("roles");
      }

    } catch (error: any) {
      console.error("Error al crear:", error.response?.data || error.message);
    }

    await fetchData();
    closeModal();
  };

  const submitRoleUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentRole.name) {
      alert("Faltan datos obligatorios.");
      return;
    }

    try {
      const payload = {
        name: currentRole.name
      };

      const response = await axios.put(`${URL_SERVER}/v3/roles/${currentRole.id}`, payload, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });

      if (response.data) {
        secureLocalStorage.removeItem("roles");
      }
    } catch (error: any) {
      console.error("Error al actualizar:", error.response?.data || error.message);
    }

    await fetchData();
    closeModal();
  };

  const deleteRole = async (id: any) => {
    try {
      const response = await axios.delete(`${URL_SERVER}/v3/roles/${id}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });

    } catch (err) {
      console.error("Error al eliminar:", err);
    }

    await fetchData();
    closeModal();
  };

  const restoreRole = async (id: any) => {
    try {
      const response = await axios.post(`${URL_SERVER}/v3/roles/${id}/restore`, null, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
    } catch (err) {
      console.error("Error al restaurar:", err);
    }

    await fetchData();
    closeModal();
  };

  const getFilteredRoles = (e: FormEvent) => {
    e.preventDefault();
    const aux = roles.filter(u =>
      u.name.toLowerCase().includes(searchRolesTerm.toLowerCase())
    );
    setRolesFiltered(aux);
  };

  const handleOrganizationChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCurrentOrganization(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchOrganization = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchOrganizationsTerm(e.target.value);
  };

  const submitOrganization = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentOrganization.name) {
      alert("Faltan datos obligatorios.");
      return;
    }

    try {
      const payload = {
        name: currentOrganization.name
      };

      const response = await axios.post(`${URL_SERVER}/v3/organizations`, payload, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });

      if (response.data) {
        secureLocalStorage.removeItem("organizations");
      }

    } catch (error: any) {
      console.error("Error al crear:", error.response?.data || error.message);
    }

    await fetchData();
    closeModal();
  };

  const submitOrganizationsUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentOrganization.name) {
      alert("Faltan datos obligatorios.");
      return;
    }

    try {
      const payload = {
        name: currentOrganization.name
      };

      const response = await axios.put(`${URL_SERVER}/v3/organizations/${currentOrganization.id}`, payload, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });

      if (response.data) {
        secureLocalStorage.removeItem("organizations");
      }
    } catch (error: any) {
      console.error("Error al actualizar:", error.response?.data || error.message);
    }

    await fetchData();
    closeModal();
  };

  const deleteOrganization = async (id: any) => {
    try {
      const response = await axios.delete(`${URL_SERVER}/v3/organizations/${id}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });

    } catch (err) {
      console.error("Error al eliminar:", err);
    }

    await fetchData();
    closeModal();
  };

  const restoreOrganization = async (id: any) => {
    try {
      const response = await axios.post(`${URL_SERVER}/v3/organizations/${id}/restore`, null, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
    } catch (err) {
      console.error("Error al restaurar:", err);
    }

    await fetchData();
    closeModal();
  };

  const getFilteredOrganizations = (e: FormEvent) => {
    e.preventDefault();
    const aux = organizations.filter(u =>
      u.name.toLowerCase().includes(searchOrganizationTerm.toLowerCase())
    );
    setOrganizationsFiltered(aux);
  };

  return {
    users,
    organizations,
    roles,
    usersFiltered,
    rolesFiltered,
    organizationsFiltered,
    modalOpen,
    currentUser,
    currentRole,
    currentOrganization,
    searchUserTerm,
    searchRolesTerm,
    searchOrganizationTerm,
    setSearchUserTerm,
    setSearchRolesTerm,
    setSearchOrganizationsTerm,
    openModal,
    closeModal,
    handleUserChange,
    handleSearchUser,
    getFilteredUsers,
    submitUser,
    submitUserUpdate,
    deleteUser,
    restoreUser,
    setCurrentUser,
    handleSearchRoles,
    handleRoleChange,
    getFilteredRoles,
    submitRole,
    submitRoleUpdate,
    deleteRole,
    restoreRole,
    setCurrentRole,
    handleSearchOrganization,
    handleOrganizationChange,
    getFilteredOrganizations,
    submitOrganization,
    submitOrganizationsUpdate,
    deleteOrganization,
    restoreOrganization,
    setCurrentOrganization,
  };
};

export default useAdminPanel;
